import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { tipoMeta, prioridadMeta } from "@/lib/alarmMeta";
import { fmtTimestamp } from "@/lib/format";

const TABS = [["persona", "Persona"], ["sitio", "Sitio"], ["global", "Global"], ["tecnico", "Técnico"]];

export default function ContextPanel({ alarma, tecnicasCount }) {
  const [tab, setTab] = useState("global");
  const [eventos, setEventos] = useState([]);
  const [nuevos, setNuevos] = useState(0);
  const enPresente = useRef(true);

  useEffect(() => { setTab(alarma ? "persona" : "global"); }, [alarma?.id]);

  const load = useCallback(async () => {
    if (tab === "tecnico") { setEventos([]); return; }
    const p = {};
    if (tab === "persona" && alarma?.persona_id) p.persona_id = alarma.persona_id;
    else if (tab === "persona" && alarma) p.sitio_id = alarma.sitio_id;
    else if (tab === "sitio" && alarma?.sitio_id) p.sitio_id = alarma.sitio_id;
    p.limit = 25;
    try {
      const { data } = await api.get("/bitacora-eventos", { params: p });
      const items = data.eventos || data || [];
      setEventos((prev) => {
        if (!enPresente.current && prev.length && items.length && items[0]?.id !== prev[0]?.id) {
          setNuevos((n) => n + 1);
          return prev;
        }
        return items;
      });
    } catch (e) { /* silencioso */ }
  }, [tab, alarma?.id, alarma?.persona_id, alarma?.sitio_id]);

  useEffect(() => { setEventos([]); setNuevos(0); load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const onScroll = (e) => { enPresente.current = e.target.scrollTop < 20; if (enPresente.current) setNuevos(0); };
  const refrescar = () => { setNuevos(0); enPresente.current = true; load(); };

  const tecnicas = tab === "tecnico";
  return (
    <div className="flex h-full flex-col" data-testid="context-panel">
      <div className="flex shrink-0 border-b border-slate-200 dark:border-slate-700">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`ctx-tab-${k}`} onClick={() => setTab(k)}
            className={`relative flex-1 px-2 py-2.5 text-xs font-semibold transition-colors ${
              tab === k ? "text-[#012472] dark:text-[#80B3FF] border-b-2 border-[#01CBE3]" : "text-slate-400 hover:text-slate-600"}`}>
            {l}
            {k === "tecnico" && tecnicasCount > 0 && (
              <span className="ml-1 rounded-full bg-[#9B2C2C] px-1.5 text-[10px] text-white">{tecnicasCount}</span>
            )}
          </button>
        ))}
      </div>
      {nuevos > 0 && (
        <button data-testid="ctx-nuevos" onClick={refrescar} className="shrink-0 bg-[#01CBE3]/15 text-[#012472] dark:text-[#80B3FF] text-xs py-1.5 font-medium">
          {nuevos} nuevo(s) · volver al presente
        </button>
      )}
      <div onScroll={onScroll} className="flex-1 overflow-y-auto p-3 space-y-2">
        {tecnicas ? (
          <div data-testid="ctx-tecnico" className="pt-4 text-center text-xs text-slate-400 space-y-2">
            <p>{tecnicasCount} incidencia(s) técnica(s) activa(s).</p>
            <p>Se atienden en la sección «Incidencias técnicas» de la cola.</p>
            <Link to="/salud" className="inline-block font-semibold text-[#012472] dark:text-[#80B3FF] underline">Ir a Salud técnica →</Link>
          </div>
        ) : eventos.length === 0 ? (
          <p className="text-xs text-slate-400 pt-4 text-center">Sin eventos recientes.</p>
        ) : eventos.map((e) => {
          const tm = tipoMeta(e.tipo_evento || e.tipo);
          const pm = prioridadMeta(e.prioridad || (e.politica_efectiva || {}).prioridad_efectiva);
          return (
            <div key={e.id} data-testid="ctx-evento" className="border-b border-slate-100 dark:border-slate-700/60 pb-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{tm.label}</span>
                <span className={`text-[10px] font-semibold uppercase ${pm.text}`}>{pm.label}</span>
              </div>
              <p className="text-[11px] text-slate-400 tabular-nums">
                {fmtTimestamp(e.hora_recepcion_servidor)}
                {!tecnicas && e.persona_nombre ? ` · ${e.persona_nombre}` : ""}
                {e.sitio_nombre ? ` · ${e.sitio_nombre}` : ""}
              </p>
            </div>
          );
        })}
      </div>
      <Link to="/bitacora" data-testid="ctx-ver-bitacora" className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-3 py-2 text-xs font-medium text-[#012472] dark:text-[#80B3FF] hover:bg-slate-50 dark:hover:bg-slate-700/50 text-center">
        Ver bitácora completa →
      </Link>
    </div>
  );
}
